/* eslint-disable no-undef */
export function initQualtricsEmbed() {
  const iframeEl = document.getElementById('qualtrics-survey-iframe');
  if (!iframeEl) {
    console.log('[Qualtrics] No iframe found with ID qualtrics-survey-iframe');
    return;
  }
  // Get the allowed origin from the iframe data attribute or src
  const originFromSrc = (iframeEl.src ? new URL(iframeEl.src).origin : '');
  const allowedOriginRaw = iframeEl.dataset.origin || originFromSrc || '';
  const allowedOrigin = allowedOriginRaw.replace(/\/+$/, ''); // normalize
  let projectSubmitted = false;

  // NEW: Hybrid approach - no survey changes required
  let hasReceivedEOS = false;
  let surveyLoadTime = Date.now(); // initial (monotonic)

  let eosReceivedTime = null;
  const MINIMUM_SURVEY_TIME = 5000; // 5 seconds minimum before allowing EOS
  const MINIMUM_EOS_DELAY = 2000; // 2 seconds minimum after load before accepting EOS

  // Store Response ID from completion events
  let storedResponseId = null;

  // If this function is called again, clean up the previous listener
  if (window.qualtricsMessageHandler) {
    window.removeEventListener('message', window.qualtricsMessageHandler);
  }

  // Reset timers when iframe actually loads
  iframeEl.addEventListener('load', () => {
    surveyLoadTime = Date.now();
    // Don't reset firstCompletionTime here - it should persist across iframe reloads
    console.log('[Qualtrics] iframe loaded; timers reset');
  });

  // Helper function to show debug messages
  function showDebugMessage(message) {
    // Create or update debug message div
    let debugDiv = document.getElementById('qualtrics-debug-message');
    if (!debugDiv) {
      debugDiv = document.createElement('div');
      debugDiv.id = 'qualtrics-debug-message';
      debugDiv.style.cssText = 'position: fixed; top: 20px; right: 20px; background: #ffc107; color: #000; padding: 15px 20px; border-radius: 5px; box-shadow: 0 2px 10px rgba(0,0,0,0.2); z-index: 10000; font-weight: bold; max-width: 400px;';
      document.body.appendChild(debugDiv);
    }
    debugDiv.textContent = message;
  }

  // Check for debug mode via URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const debugMode = urlParams.has('qualtrics_debug');

  if (debugMode) {
    console.log('[Qualtrics] DEBUG MODE ENABLED - Redirects will be prevented');
    console.log('[Qualtrics] To exit debug mode, remove ?qualtrics_debug from the URL');
    showDebugMessage('Qualtrics Debug Mode Active - Check console for detailed logs');
  }

  console.log('[Qualtrics] Initialized with:', {
    origin: allowedOrigin,
    postUrl: iframeEl.dataset.postUrl,
    redirectUrl: iframeEl.dataset.redirect,
    iframeSrc: iframeEl.src,
    debugMode: debugMode
  });

  function extractIdFromString(str) {
    if (!str) return null;

    // Check for pipe-delimited format: QualtricsEOS|SurveyID|ResponseID
    // ResponseID is a built-in Qualtrics field (doesn't need embedded data)
    // The third part is the Response ID (R_xxx) - what we want!
    if (str.includes('|')) {
      const parts = str.split('|');
      if (parts.length >= 3) {
        return parts[2];
      }
    }

    // Look for Response ID (R_ prefix)
    const match = str.match(/R_[A-Za-z0-9]+/);
    return match ? match[0] : null;
  }

  function submitProjectAjax(url, csrfToken, qualtricsId) {
    if (projectSubmitted) {
      console.log('[Qualtrics] Submit already in progress, skipping');
      return;
    }
    projectSubmitted = true;

    // Get submission UUID from iframe data attribute
    const submissionUuid = iframeEl.dataset.submissionUuid;

    // Get placeholder values from iframe data attributes
    const placeholderName = iframeEl.dataset.placeholderName || 'Application Processing';
    const placeholderDescription = iframeEl.dataset.placeholderDescription || 'Processing your application';

    const payload = {
      project: {
        name: placeholderName,
        title: placeholderName + ' - ' + new Date().toLocaleString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hour12: true
        }),
        description: placeholderDescription,
        submission_uuid: submissionUuid
      }
    };

    // Handle ID from Qualtrics postMessage
    // With ResponseID in embedded data, we should now get Response IDs (R_xxx)
    if (qualtricsId) {
      if (qualtricsId.startsWith('R_')) {
        // We have the actual response ID! This is what we want.
        payload.project.qualtrics_response_id = qualtricsId;
        console.log('[Qualtrics] Received response ID:', qualtricsId);
      } else {
        // Unknown format - log it
        console.log('[Qualtrics] Received unknown ID format:', qualtricsId);
      }
    } else {
      // No ID detected, this might be a fallback submission
      console.log('[Qualtrics] No ID found in postMessage');
    }

    console.log('[Qualtrics] Submission UUID:', submissionUuid);

    console.log('[Qualtrics] Submitting project with payload:', JSON.stringify(payload, null, 2));

    // Enhanced debug logging
    if (debugMode) {
      console.group('[Qualtrics] Debug Summary');
      console.log('Response ID:', payload.project.qualtrics_response_id || 'none');
      console.log('Submission UUID:', submissionUuid);
      console.log('ID Type:', qualtricsId ? 'Response' : 'None');
      console.log('Submit URL:', url);
      console.log('CSRF Token Present:', !!csrfToken);
      console.log('Debug Mode:', debugMode);
      console.log('Redirect URL:', iframeEl.dataset.redirect);
      console.log('Timestamp:', new Date().toISOString());
      console.groupEnd();
    }

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify(payload)
    })
      .then(async (response) => {
        const contentType = response.headers.get('content-type') || '';
        console.log('[Qualtrics] Response received:', {
          status: response.status,
          contentType: contentType,
          ok: response.ok
        });

        const body = contentType.includes('application/json') ? await response.json() : {};
        console.log('[Qualtrics] Response body:', body);

        // For Qualtrics submissions, we always treat as success from user's perspective
        // Backend will always return success: true to avoid blocking user workflow
        if (body.status === 'success' || response.ok) {
          console.log('[Qualtrics] Success! Response:', body);
          if (debugMode) {
            console.log('[Qualtrics] DEBUG MODE - Preventing redirect. Would redirect to:', iframeEl.dataset.redirect);
            showDebugMessage('Success! Check console for details. Redirect prevented due to debug mode.');
          } else {
            console.log('[Qualtrics] Redirecting to:', iframeEl.dataset.redirect);
            window.location.href = iframeEl.dataset.redirect;
          }
        } else {
          // This should never happen with our new backend logic, but just in case
          console.error('[Qualtrics] Unexpected response:', body);

          // Log validation errors if present
          if (body.errors && Array.isArray(body.errors)) {
            console.error('[Qualtrics] Validation errors:', body.errors);
            body.errors.forEach((error, index) => {
              console.error('  ' + (index + 1) + '. ' + error);
            });
          }

          if (debugMode) {
            console.log('[Qualtrics] DEBUG MODE - Preventing redirect. Would redirect to:', iframeEl.dataset.redirect);
            showDebugMessage('Unexpected response! Check console for validation errors. Redirect prevented due to debug mode.');
          } else {
            // Still redirect - don't block the user
            window.location.href = iframeEl.dataset.redirect;
          }
        }
      })
      .catch((err) => {
        console.error('Project submit network error:', err);
        // For Qualtrics submissions, we don't want to block the user
        // Log the error but still redirect them
        if (debugMode) {
          console.log('[Qualtrics] DEBUG MODE - Network error occurred. Preventing redirect. Would redirect to:', iframeEl.dataset.redirect);
          showDebugMessage('Network error! Check console for details. Redirect prevented due to debug mode.');
        } else {
          console.log('[Qualtrics] Network error occurred, but redirecting anyway');
          window.location.href = iframeEl.dataset.redirect;
        }
      });
  }

  // Store the message handler so we can remove it if needed
  window.qualtricsMessageHandler = function (event) {
    // Check origin first to avoid logging non-Qualtrics messages
    if (allowedOrigin && event.origin !== allowedOrigin) {
      // Only log if it looks like it might be Qualtrics-related
      if (event.data && (event.data.QualtricsEOS || event.data.status === 'complete')) {
        console.log('[Qualtrics] Received message from unexpected origin:', event.origin, 'expected:', allowedOrigin);
      }
      return;
    }

    // Check if from the survey iframe
    if (event.source !== iframeEl.contentWindow) {
      return;
    }


    // Log all messages for debugging
    console.log('[Qualtrics] Received message from iframe:', {
      data: event.data,
      type: typeof event.data,
      timeSinceLoad: Date.now() - surveyLoadTime
    });

    // Handle EOS message with timing controls
    if (typeof event.data === 'string' && event.data.startsWith('QualtricsEOS|')) {
      hasReceivedEOS = true;
      eosReceivedTime = Date.now();

      // Extract session ID from EOS message (this will be FS_ format)
      const sessionId = extractIdFromString(event.data);
      console.log('[Qualtrics] EOS message session ID:', sessionId || 'none');

      // Use stored Response ID for submission, fallback to session ID if no Response ID stored
      const responseIdForSubmission = storedResponseId || sessionId;
      console.log('[Qualtrics] Will use for submission:', responseIdForSubmission,
        storedResponseId ? '(stored Response ID)' : '(session ID fallback)');

      // Check now, or schedule when the minimum time elapses
      const timeDiff = MINIMUM_SURVEY_TIME - (Date.now() - surveyLoadTime);
      const msUntilMinTime = timeDiff > 0 ? timeDiff : 0;
      if (msUntilMinTime > 0) {
        console.log('[Qualtrics] EOS early; scheduling submit in ' + msUntilMinTime + 'ms');
        setTimeout(() => {
          if (canSubmitSurvey()) {
            const csrfToken = (document.querySelector('meta[name="csrf-token"]') && document.querySelector('meta[name="csrf-token"]').content) || '';
            submitProjectAjax(iframeEl.dataset.postUrl, csrfToken, responseIdForSubmission);
          }
        }, msUntilMinTime);
      } else {
        // We can submit immediately
        if (canSubmitSurvey()) {
          const csrfToken = (document.querySelector('meta[name="csrf-token"]') && document.querySelector('meta[name="csrf-token"]').content) || '';
          submitProjectAjax(iframeEl.dataset.postUrl, csrfToken, responseIdForSubmission);
        }
      }
      return;
    }

    // Debug logging only for messages that pass initial checks
    console.log('[Qualtrics] Received postMessage:', {
      origin: event.origin,
      data: event.data
    });

    try {
      let data = event.data;
      let isString = typeof data === 'string';
      let parsed = data;

      console.log('[Qualtrics] Data type:', isString ? 'string' : typeof data);

      if (isString) {
        try {
          parsed = JSON.parse(data);
          console.log('[Qualtrics] Successfully parsed string to JSON:', parsed);
        } catch (e) {
          console.log('[Qualtrics] Could not parse string as JSON, treating as plain string');
        }
      }

      let isComplete = false;
      let qualtricsId = null;
      let completionReason = null;

      if (isString && parsed === data) {
        // Still a string after parse attempt
        const str = data;
        console.log('[Qualtrics] Checking string for completion markers...');
        if (str.includes('QualtricsEOS')) {
          isComplete = true;
          completionReason = 'String contains QualtricsEOS';
        } else if (str.includes('closeQSIWindow')) {
          isComplete = true;
          completionReason = 'String contains closeQSIWindow';
        }
        if (isComplete) {
          qualtricsId = extractIdFromString(str);
          console.log('[Qualtrics] Extracted ID from string:', qualtricsId,
            qualtricsId ? '(' + (qualtricsId.startsWith('R_') ? 'Response' : 'Session') + ' ID)' : '');
        }
      } else if (parsed && typeof parsed === 'object') {
        // Handle both parsed strings and direct objects
        console.log('[Qualtrics] Checking object for completion...', {
          status: parsed.status,
          completedSurvey: parsed.completedSurvey,
          type: parsed.type,
          event: parsed.event
        });

        if (parsed.status === 'complete') {
          isComplete = true;
          completionReason = 'status === complete';
        } else if (parsed.completedSurvey === true) {
          isComplete = true;
          completionReason = 'completedSurvey === true';
        } else if (parsed.type === 'QualtricsSurveyComplete') {
          isComplete = true;
          completionReason = 'type === QualtricsSurveyComplete';
        } else if (parsed.type === 'qualtrics:completed') {
          isComplete = true;
          completionReason = 'type === qualtrics:completed (custom)';
        } else if (parsed.event === 'QualtricsEOS') {
          isComplete = true;
          completionReason = 'event === QualtricsEOS';
        }

        // Check all possible response ID locations
        // NOTE: ResponseID is a built-in Qualtrics field - it's always available in responses
        // regardless of whether it's in the survey's embedded data configuration
        console.log('[Qualtrics] Checking for response ID in:', {
          responseId: parsed.responseId,
          responseID: parsed.responseID,
          Q_R: parsed.Q_R,
          QResponse: parsed.QResponse,
          QualtricsResponseID: parsed.QualtricsResponseID,
          response: parsed.response
        });

        qualtricsId = (
          parsed.responseId || parsed.responseID || parsed.Q_R ||
          (parsed.QResponse && parsed.QResponse.QID) || parsed.QualtricsResponseID ||
          (parsed.response && parsed.response.responseId)
        ) || null;

        // Special handling for our custom message format
        if (parsed.type === 'qualtrics:completed' && parsed.submission_uuid) {
          // Verify this is for the correct submission
          if (parsed.submission_uuid === iframeEl.dataset.submissionUuid) {
            console.log('[Qualtrics] Custom message matched submission UUID');
          }
        }
      }

      if (!isComplete) {
        console.log('[Qualtrics] Message not a completion event - ignoring');
        return;
      }



      // Store the Response ID if we found one
      if (qualtricsId && qualtricsId.startsWith('R_')) {
        storedResponseId = qualtricsId;
        console.log('[Qualtrics] Stored Response ID:', storedResponseId);
      }

      console.log('[Qualtrics] Survey completed!', {
        reason: completionReason,
        id: qualtricsId || 'NOT FOUND',
        idType: qualtricsId ? 'Response ID' : 'none',
        willUseTempId: !qualtricsId,
        timeSinceLoad: Math.round(Date.now() - surveyLoadTime) + 'ms',
        storedResponseId: storedResponseId
      });

      // Completion events can trigger submission if EOS has already been received
      if (hasReceivedEOS) {
        console.log('[Qualtrics] EOS already received, completion event can trigger submission');

        // Check timing before submitting
        const timeSinceLoad = Date.now() - surveyLoadTime;
        if (timeSinceLoad < MINIMUM_SURVEY_TIME) {
          const msUntilMinTime = MINIMUM_SURVEY_TIME - timeSinceLoad;
          console.log('[Qualtrics] Completion too early; scheduling submit in ' + msUntilMinTime + 'ms');
          setTimeout(() => {
            if (canSubmitSurvey()) {
              const csrfToken = (document.querySelector('meta[name="csrf-token"]') && document.querySelector('meta[name="csrf-token"]').content) || '';
              submitProjectAjax(iframeEl.dataset.postUrl, csrfToken, qualtricsId);
            }
          }, msUntilMinTime);
        } else {
          // We can submit immediately
          if (canSubmitSurvey()) {
            const csrfToken = (document.querySelector('meta[name="csrf-token"]') && document.querySelector('meta[name="csrf-token"]').content) || '';
            submitProjectAjax(iframeEl.dataset.postUrl, csrfToken, qualtricsId);
          }
        }
      } else {
        // EOS not received yet, just store the Response ID and wait
        console.log('[Qualtrics] Completion event detected but not triggering submission (waiting for real EOS message)');
      }
    } catch (err) {
      console.error('PostMessage handler error:', err);
    }
  };

  // Add the message listener
  window.addEventListener('message', window.qualtricsMessageHandler);

  // Helper function to check if we can submit the survey
  function canSubmitSurvey() {
    if (projectSubmitted) {
      console.log('[Qualtrics] Project already submitted, skipping');
      return false;
    }

    const timeSinceLoad = Date.now() - surveyLoadTime;
    if (timeSinceLoad < MINIMUM_SURVEY_TIME) {
      console.log('[Qualtrics] Survey time too short (' + Math.round(timeSinceLoad) + 'ms < ' + MINIMUM_SURVEY_TIME + 'ms), cannot submit yet');
      return false;
    }

    // Additional safety check - ensure we've actually received a completion message
    if (!hasReceivedEOS) {
      console.log('[Qualtrics] No EOS message received yet, cannot submit');
      return false;
    }

    return true;
  }
}

document.addEventListener('DOMContentLoaded', initQualtricsEmbed);


